/**
 * Composable for fetching and processing location availability data
 * Combines master data and booking data to provide real-time location status
 */

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useMasterDataQuery, fetchMasterData } from '../api/queries/masterDataQueries.js';
import { fetchAllBookings } from '../api/queries/bookingQueries.js';
import { processLocationData } from '../api/utils/locationAggregation.js';

/**
 * Composable for location data management
 * @param {Object} options - Configuration options
 * @param {number} options.refreshInterval - How often to refresh data (ms)
 * @param {boolean} options.autoRefresh - Whether to auto-refresh data
 * @returns {Object} Location data and state
 */
export const useLocationData = (options = {}) => {
	const {
		refreshInterval = 1000 * 60 * 2, // 2 minutes default
		autoRefresh = true
	} = options;

	// Current time tracking
	const currentTime = ref(new Date());
	let timeInterval = null;

	// Update current time every minute for real-time calculations
	const updateCurrentTime = () => {
		currentTime.value = new Date();
	};

	// Auto-refresh timer
	const startTimeUpdate = () => {
		if (autoRefresh && !timeInterval) {
			updateCurrentTime();
			timeInterval = setInterval(updateCurrentTime, 60000); // Update every minute
		}
	};

	const stopTimeUpdate = () => {
		if (timeInterval) {
			clearInterval(timeInterval);
			timeInterval = null;
		}
	};

	// Format today's date for API calls
	const today = computed(() => {
		return currentTime.value.toISOString().split('T')[0];
	});

	// Helper to get today's date string (used for query to ensure it always fetches for today)
	const getTodayString = () => new Date().toISOString().split('T')[0];

	// Master data query
	const masterDataQuery = useQuery({
		...useMasterDataQuery(),
		refetchInterval: refreshInterval
	});

	// All bookings query for today - always fetches for current day
	const bookingsQuery = useQuery({
		queryKey: ['room-booking-management', 'bookings', 'all', { date: getTodayString() }],
		queryFn: () => fetchAllBookings(getTodayString()),
		staleTime: 1000 * 10,
		refetchInterval: 1000 * 15,
		gcTime: 1000 * 60 * 15,
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
	});

	// Watch for date changes and refetch bookings
	watch(today, () => {
		bookingsQuery.refetch();
	});

	// Process the raw data into location objects
	const locations = computed(() => {
		if (!masterDataQuery.data?.value || !bookingsQuery.data?.value) {
			return [];
		}

		try {
			return processLocationData(
				masterDataQuery.data.value,
				bookingsQuery.data.value,
				currentTime.value
			);
		} catch (error) {
			console.error('Error processing location data:', error);
			return [];
		}
	});

	// Loading states
	const isLoading = computed(() => {
		return masterDataQuery.isLoading.value || bookingsQuery.isLoading.value;
	});

	const isError = computed(() => {
		return masterDataQuery.isError.value || bookingsQuery.isError.value;
	});

	const error = computed(() => {
		return masterDataQuery.error.value || bookingsQuery.error.value;
	});

	// Refresh function
	const refresh = async () => {
		updateCurrentTime();
		await Promise.all([
			masterDataQuery.refetch(),
			bookingsQuery.refetch()
		]);
	};

	// Statistics
	const locationStats = computed(() => {
		if (!locations.value.length) {
			return {
				totalLocations: 0,
				openLocations: 0,
				closedLocations: 0,
				totalAvailableRooms: 0,
				totalRooms: 0
			};
		}

		return locations.value.reduce((stats, location) => {
			return {
				totalLocations: stats.totalLocations + 1,
				openLocations: stats.openLocations + (location.isOpen ? 1 : 0),
				closedLocations: stats.closedLocations + (!location.isOpen ? 1 : 0),
				totalAvailableRooms: stats.totalAvailableRooms + location.availableRooms,
				totalRooms: stats.totalRooms + (location.totalRooms || 0)
			};
		}, {
			totalLocations: 0,
			openLocations: 0,
			closedLocations: 0,
			totalAvailableRooms: 0,
			totalRooms: 0
		});
	});

	// Lifecycle management
	onMounted(() => {
		startTimeUpdate();
	});

	onUnmounted(() => {
		stopTimeUpdate();
	});

	// Manual time update for testing
	const updateTime = (newTime) => {
		if (newTime instanceof Date) {
			currentTime.value = newTime;
		}
	};

	return {
		// Data
		locations,
		locationStats,
		currentTime: computed(() => currentTime.value),
		today,

		// Loading states
		isLoading,
		isError,
		error,

		// Actions
		refresh,
		updateTime,

		// Raw query objects (for advanced usage)
		masterDataQuery,
		bookingsQuery,

		// Lifecycle
		startTimeUpdate,
		stopTimeUpdate
	};
};

/**
 * Lightweight composable for just location data without auto-refresh
 * Useful for components that don't need real-time updates
 */
export const useStaticLocationData = () => {
	return useLocationData({
		autoRefresh: false,
		refreshInterval: 0
	});
};

/**
 * Get location data once without reactivity
 * Useful for server-side rendering or one-time data fetching
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Array>} Processed location data
 */
export const getLocationDataOnce = async (date = null) => {
	const targetDate = date || new Date().toISOString().split('T')[0];
	
	try {
		const [masterData, bookingsData] = await Promise.all([
			fetchMasterData(),
			fetchAllBookings(targetDate)
		]);

		return processLocationData(masterData, bookingsData, new Date());
	} catch (error) {
		console.error('Error fetching location data:', error);
		throw error;
	}
};